/**
 * "Ver mais" — insight details, per the prototype's `#insightDetailModal`.
 *
 * The prototype fills its two content blocks from a hand-written dictionary
 * (`INSIGHT_DETAIL_FACTS`) because it has no data. We have both:
 *
 * - "O que a IA identificou" ← `related_metrics`, the numbers the rule that
 *   raised the insight actually measured.
 * - "Ação recomendada" ← `recommended_actions`.
 *
 * `related_metrics` is empty on some insights (not every branch of
 * `insights_service` fills it), so that block hides itself — which is what
 * the prototype does when its own facts list is empty.
 */

import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { useNavigation } from '../../hooks/useNavigation';

import { insightAreaLink, severityTone } from './insightPresentation';

function InsightDetailModal({ insight, onClose, onDismiss, onChat }) {
  const { t } = useTranslation();
  const { navigateToTenant } = useNavigation();

  useEffect(() => {
    if (!insight) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [insight, onClose]);

  if (!insight) return null;

  const tone = severityTone(insight.severity);
  const area = insightAreaLink(insight.category);
  const metrics = Object.entries(insight.related_metrics || {});
  const actions = insight.recommended_actions || [];

  return (
    <div className="modal open" id="insightDetailModal" aria-hidden="false">
      <button
        type="button"
        className="modal-backdrop"
        aria-label={t('insights.detail.close')}
        onClick={onClose}
      />
      <div
        className="modal-dialog insight-detail-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="insightDetailTitle"
      >
        <div className="modal-header">
          <div className="insight-detail-heading">
            <span className={`chip ${tone}`}>{t(`insights.severity.${insight.severity}`)}</span>
            <h3 id="insightDetailTitle">{insight.title}</h3>
          </div>
          <button
            type="button"
            className="icon-btn"
            aria-label={t('insights.detail.close')}
            onClick={onClose}
          >
            <span className="material-symbols-rounded" aria-hidden="true">close</span>
          </button>
        </div>

        <div className="modal-body insight-detail-body">
          <p className="insight-detail-desc">{insight.description}</p>

          {metrics.length > 0 ? (
            <div className="insight-detail-block">
              <h4>{t('insights.detail.evidence')}</h4>
              <ul className="insight-detail-list" data-testid="insight-detail-metrics">
                {metrics.map(([key, value]) => (
                  <li key={key}>
                    <span>{t(`insights.metrics.${key}`, { defaultValue: key })}</span>
                    <strong>{String(value)}</strong>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {actions.length > 0 ? (
            <div className="insight-detail-block">
              <h4>{t('insights.detail.recommendation')}</h4>
              {actions.map((action) => (
                <p className="insight-action" key={action}>
                  <span className="material-symbols-rounded" aria-hidden="true">lightbulb</span>
                  <span>{action}</span>
                </p>
              ))}
            </div>
          ) : null}

          {/* Router navigation, not an anchor: the app runs a BrowserRouter,
              so a plain href would reload the whole SPA. `navigateToTenant`
              prefixes the tenant slug, the same way the sidebar navigates. */}
          {area ? (
            <button
              type="button"
              className="insight-more-link insight-detail-link"
              onClick={() => {
                onClose?.();
                navigateToTenant(area.path);
              }}
              data-testid="insight-detail-area"
            >
              {t('insights.detail.openArea', { area: t(`insights.category.${area.key}`) })}
            </button>
          ) : null}
        </div>

        <div className="modal-footer">
          <button
            className="btn outline"
            type="button"
            onClick={() => onDismiss?.(insight)}
            data-testid="insight-detail-dismiss"
          >
            <span className="material-symbols-rounded" aria-hidden="true">close</span>
            {t('insights.dismiss')}
          </button>
          <button
            className="btn primary"
            type="button"
            onClick={() => onChat?.(insight)}
            data-testid="insight-detail-chat"
          >
            <span className="material-symbols-rounded" aria-hidden="true">forum</span>
            {t('insights.chat')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default InsightDetailModal;
